<template>
  <v-card flat color="transparent" style="min-height: 50rem;" :loading="loading">
    <v-row>
      <v-col cols="12" class="d-flex justify-center ma-2">
        <v-text-field
          :label="$t('portfolio.general.searchIn', { title: $t('portfolio.Achievement.plural') })"
          v-model="search"
          append-icon="mdi-magnify"
          outlined
          dense
          hide-details
        />
      </v-col>
    </v-row>
    <v-row  class="ma-0 pa-0">
      <v-col
        cols="12"
        v-if="achievements && achievements.length > 0 && filteredAchievements.length > 0"
      >
        <v-row>
          <v-col
            cols="12"
            v-for="(achievement, index) in paginatedAchievements"
            :key="achievement.achievementGuid"
            lg="3"
            md="4"
            sm="12"
            xs="12"
          >
            <BaseCard
              height="100%"
              icon="mdi-trophy"
              :showIcons="$store.state.userData.guid === achievement.student.studentGuid"
              :key="achievement.achievementGuid"
              :title="achievement.title"
              :description="achievement.description"
              :symmetricHeight="false"
              @details="navToDetails(achievement)"
              @delete="deleteAchievement(achievement)"
              @edit="
                $refs.addAchievementDialog.showDialogEdit(achievement)
              "
            >
              <template #content>
                <VDivider />
                <v-chip
                  class="ma-2 align-self-start"
                  label
                  :color="$vuetify.theme.dark ? 'green darken-4' : 'green lighten-5'"
                >
                  {{ $t("portfolio.Achievement.date") }} :
                  {{ achievement.achievementDate | moment("YYYY/MM/DD") }}
                </v-chip>
                 <div class="d-flex">
                    <div class="ma-2">
                      <TagsMini v-model="achievement.tags" :sourceGuid="achievement.achievementGuid" sourceType="Achievement" :showAdd="isInRole(0)" :isRemovable="isInRole(0)"   />
                    </div>
                     
                </div>
                <div class="d-flex">
                  <v-spacer />

                  <v-chip
                    class="my-2"
                    label
                    color="transparent"
                    small
                    :title="achievement.createdDate | moment('YYYY/MM/DD - h:mm A')"
                  >
                    {{ dateFromNow(achievement.createdDate) }}
                  </v-chip>
                  
                </div>
              </template>
            </BaseCard>
          </v-col>
        </v-row>

        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          :total-visible="$vuetify.breakpoint.smAndDown ? 1 : 3"
          class="mx-auto my-5"
        />
      </v-col>
      <v-col cols="12" v-else-if="!loading">
        <NoData />
      </v-col>
    </v-row>

    <AchievementsDialog ref="addAchievementDialog" @success="getAchievements" />
    <ConfirmActionDialogue
      ref="confirmActionDialog"
      :title="$t('portfolio.Achievement.confirmDelete')"
      :description="
        $t('portfolio.Achievement.confirmDeleteMessage', {
          title: activeAchievement ? activeAchievement.title : ''
        })
      "
      @refresh="getAchievements"
      @confirm="deleteAchievementConfirmed"
    />

    <v-fab-transition v-if="this.studentGuid === this.$store.state.userData.guid">
      <v-btn
        :title="$t('portfolio.Achievement.addAchievement')"
        color="secondary"
        fab
        large
        elevation="6"
        fixed
        bottom
        :left="$vuetify.rtl"
        :right="!$vuetify.rtl"
        class="v-btn--example"
        :class="$store.state.drawerMessage ? 'ml-16' : ''"
        :style="!$vuetify.rtl ? 'right:50px' : ''"
        @click="$refs.addAchievementDialog.showDialog()"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-fab-transition>
  </v-card>
</template>

<script>
import NoData from "@/components/noData.vue";
import BaseCard from "../../../components/portfolio/baseCard.vue";
import SearchTextField from "../../../components/portfolio/searchTextfield.vue";
import AchievementsDialog from "../../../components/portfolio/achievements/achievementsDialog.vue";
import ConfirmActionDialogue from "../../../components/confirmActionDialog.vue";
import LoadingData from "../../../components/loadingData.vue";
import TagsMini from "../../../components/portfolio/tagsMini.vue";

export default {
  name: "Achievements",
  props: {
    studentGuid: {
      type: String,
      required: true
    }
  },
  components: {
    BaseCard,
    SearchTextField,
    AchievementsDialog,
    ConfirmActionDialogue,
    NoData,
    LoadingData,
    TagsMini
  },
  data() {
    return {
      activeAchievement: null,
      achievements: [],
      loading: true,
      currentPage: 1,
      itemsPerPage: 4,
      search: ""
    };
  },
  mounted() {
    this.getAchievements();
  },
  methods: {
    navToDetails(achievement) {
      this.$router.push({
        name: "portfolio.Achievement.details",
        params: { id: achievement.achievementGuid }
      });
    },
    getAchievements() {
      this.loading = true;
      this.axios
        .get("/Achievement/Get", { params: { studentGuid: this.studentGuid } })
        .then(response => {
          this.achievements = response.data.data;
        })
        .catch(error => {
          console.error("Error fetching achievements:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    deleteAchievement(achievement) {
      this.activeAchievement = achievement;
      this.$refs.confirmActionDialog.showDialog();
    },
    deleteAchievementConfirmed() {
      this.axios
        .post("/Achievement/Delete", { achievementGuid: this.activeAchievement.achievementGuid })
        .then(() => {
          this.getAchievements();
        })
        .catch(error => {
          console.error("Error deleting achievement:", error);
        });
    },
    searchInput(search) {
      this.search = search;
      this.currentPage = 1;
    }
  },
  computed: {
    totalPages() {
      const filtered = this.filteredAchievements;
      return Math.ceil(filtered.length / this.itemsPerPage);
    },
    paginatedAchievements() {
      const filtered = this.filteredAchievements;
      if (filtered.length === 0) return [];
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      if (end > filtered.length) {
        return filtered.slice(start);
      }
      if (start > filtered.length) {
        return [];
      }
      return filtered.slice(start, end);
    },
    filteredAchievements() {
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        return this.achievements.filter(
          achievement =>
            (achievement.title && achievement.title.toLowerCase().includes(searchLower)) ||
            (achievement.description && achievement.description.toLowerCase().includes(searchLower))
        );
      }
      return this.achievements;
    }
  }
};
</script>

<style scoped>
.achievements {
  padding: 20px;
}

.samples-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.wrapped-text {
  word-break: normal;
  overflow-wrap: normal;
  white-space: normal;
}
</style>
